import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CONFIG } from '../config';
import { AuthContext } from '../contexts/AuthContext';

// Déduplication courte pour éviter les doubles appels (StrictMode) et re-rendus rapides
const INFLIGHT = new Map(); // key -> Promise
const SHORT_CACHE = new Map(); // key -> { data, expiry }
const TTL_MS = 1500; // 1.5s

async function fetchMaintenanceStatus() {
  const key = 'maintenance-check';
  const now = Date.now();
  const cached = SHORT_CACHE.get(key);
  if (cached && now < cached.expiry) return cached.data;

  if (!INFLIGHT.has(key)) {
    const p = (async () => {
      const baseUrl = String(CONFIG?.API_URL || '').replace(/\/+$/, '');
      const maintenanceUrl = `${baseUrl}/api/public/maintenance`;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const res = await fetch(maintenanceUrl, {
          headers: { 'Accept': 'application/json' },
          cache: 'no-store',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!res.ok) {
          // Réponse HTTP non-200 : on ne bloque pas l'app
          const data = { maintenance: false, updatedAt: null, reason: null };
          SHORT_CACHE.set(key, { data, expiry: Date.now() + TTL_MS });
          return data;
        }

        const json = await res.json().catch(() => null);
        const data = {
          maintenance: Boolean(json?.maintenance),
          updatedAt: typeof json?.updatedAt === 'string' ? json.updatedAt : null,
          reason: typeof json?.reason === 'string' ? json.reason : null,
        };
        SHORT_CACHE.set(key, { data, expiry: Date.now() + TTL_MS });
        return data;
      } catch {
        // Erreur réseau, timeout, abort : on ne bloque PAS l'app.
        // La gestion offline est assurée par connection-status.js
        const data = { maintenance: false, updatedAt: null, reason: null };
        SHORT_CACHE.set(key, { data, expiry: Date.now() + TTL_MS });
        return data;
      }
    })().finally(() => {
      INFLIGHT.delete(key);
    });
    INFLIGHT.set(key, p);
  }
  return INFLIGHT.get(key);
}

const MaintenanceChecker = ({ children }) => {
  const location = useLocation();
  const { profile } = useContext(AuthContext);
  const isAdminRoute = location.pathname.startsWith('/admin')
    || (location.pathname === '/login' && new URLSearchParams(location.search).get('redirect')?.startsWith('/admin'));
  const hasMaintenanceBypass = isAdminRoute || profile?.role === 'admin';

  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState({ updatedAt: null, reason: null });

  useEffect(() => {
    const check = async () => {
      try {
        const data = await fetchMaintenanceStatus();
        if (data && data.maintenance === true) {
          setMaintenanceInfo({ updatedAt: data.updatedAt || null, reason: data.reason || null });
          setIsMaintenance(true);
        } else {
          setIsMaintenance(false);
        }
      } catch {
        // En cas d'erreur inattendue, ne pas bloquer l'app
        setIsMaintenance(false);
      }
    };

    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  // Les routes admin et les profils admin ne sont jamais bloqués par la maintenance.
  if (hasMaintenanceBypass) {
    return children;
  }

  // Bloquer l'app UNIQUEMENT si l'API a répondu maintenance: true
  if (isMaintenance) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '560px',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>
            🛠️
          </div>
          <h1 style={{ fontSize: '28px', marginBottom: '12px', fontWeight: 700 }}>
            Maintenance en cours
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.95, lineHeight: 1.6, marginBottom: '16px' }}>
            Magic Park Times est momentanément en maintenance. Merci de réessayer dans quelques instants.
          </p>

          {maintenanceInfo?.reason && (
            <p style={{
              fontSize: '14px',
              opacity: 0.9,
              background: 'rgba(0,0,0,0.15)',
              padding: '10px 12px',
              borderRadius: '10px',
              marginBottom: '12px'
            }}>
              {maintenanceInfo.reason}
            </p>
          )}

          {maintenanceInfo?.updatedAt && (
            <p style={{ fontSize: '12px', opacity: 0.8, marginBottom: '18px' }}>
              Dernière mise à jour : {maintenanceInfo.updatedAt}
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: 'white',
                color: '#667eea',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              style={{
                background: 'rgba(255,255,255,0.18)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.35)',
                padding: '12px 20px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default MaintenanceChecker;
